'use client';

import { useQueryClient } from '@tanstack/react-query';
import { Unlink } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import {
  useDisconnectSentryMutation,
  useSaveSentrySelectionMutation,
  useSentryConnection,
  useSentryOrganizations,
  useSentryProjects,
} from '../hooks/queries/useSentry';
import { queryKeys } from '../hooks/queries/queryKeys';
import { buildSentryAuthConnectUrl, isSentryReconnectRequired } from '../lib/api/sentry';
import { ApiError } from '../lib/api/error';
import { getAuthSession } from '../lib/authSession';
import {
  buildSentryOauthPopupRedirectPath,
  openSentryOauthPopup,
  sentryOauthPopupEventType,
} from '../lib/sentryOAuthPopup';
import { ConnectionStatusChip } from './ConnectionStatusChip';
import DeleteConfirmModal from './DeleteConfirmModal';
import { Select } from './Select';
import { SentryIcon } from './icons/ProviderIcons';
import { Spinner } from './loading/Spinner';

type SentryIntegrationProps = { projectId: string; canManage: boolean };

/**
 * Sentry 연결/선택 패널. 통합 화면의 "리포지토리 추가" 탭 스트립 안에서 렌더된다.
 *
 * 다른 provider(GitHub/Linear 등)와 같은 2단계 규칙을 따른다:
 * 여기서 연결하고 대상을 고르면, 결과는 아래 목록의 행(SentryConnectedRow)으로 내려간다.
 */
export function SentryConnectPanel({ projectId, canManage }: SentryIntegrationProps) {
  const t = useTranslations('projects.sentry');
  const session = useMemo(() => getAuthSession(), []);
  const token = session?.token ?? null;
  const queryClient = useQueryClient();
  const connectionQuery = useSentryConnection(token, projectId);
  const connection = connectionQuery.data;
  // 선택 후보 열거는 관리 권한 전용 API다. 비관리자에게는 요청 자체를 보내지 않는다.
  const canListCandidates = canManage && connection?.isConnected === true;
  const organizationsQuery = useSentryOrganizations(token, projectId, canListCandidates);
  const [organizationId, setOrganizationId] = useState('');
  const organization = organizationsQuery.data?.find((item) => item.id === organizationId);
  const projectsQuery = useSentryProjects(token, projectId, organization?.slug ?? '', canListCandidates);
  const [sentryProjectId, setSentryProjectId] = useState('');
  const [oauthPending, setOauthPending] = useState(false);
  const oauthCompletedRef = useRef(false);
  const oauthMonitorRef = useRef<number | null>(null);
  const saveSelection = useSaveSentrySelectionMutation(token, projectId);

  useEffect(() => {
    const organizations = organizationsQuery.data ?? [];
    if (organizations.length === 0) {
      setOrganizationId('');
      return;
    }
    setOrganizationId((current) => {
      if (organizations.some((item) => item.id === current)) return current;
      return organizations.find((item) => item.id === connection?.organizationId)?.id ?? organizations[0]!.id;
    });
  }, [connection?.organizationId, organizationsQuery.data]);

  useEffect(() => {
    const projects = projectsQuery.data ?? [];
    if (projects.length === 0) {
      setSentryProjectId('');
      return;
    }
    setSentryProjectId((current) => {
      if (projects.some((item) => item.id === current)) return current;
      return projects.find((item) => item.id === connection?.projectId)?.id ?? projects[0]!.id;
    });
  }, [connection?.projectId, projectsQuery.data]);

  useEffect(() => {
    const handleComplete = () => {
      oauthCompletedRef.current = true;
      if (oauthMonitorRef.current !== null) {
        window.clearInterval(oauthMonitorRef.current);
        oauthMonitorRef.current = null;
      }
      setOauthPending(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.sentry.all(projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.search.all(projectId) });
      toast.success(t('oauthCompleted'));
    };
    window.addEventListener(sentryOauthPopupEventType, handleComplete);
    return () => {
      window.removeEventListener(sentryOauthPopupEventType, handleComplete);
      if (oauthMonitorRef.current !== null) window.clearInterval(oauthMonitorRef.current);
    };
  }, [projectId, queryClient, t]);

  const connect = () => {
    if (!token || !canManage) return;
    const nextPath = `${window.location.pathname}${window.location.search}`;
    const url = buildSentryAuthConnectUrl(token, buildSentryOauthPopupRedirectPath(nextPath), projectId);
    const popup = openSentryOauthPopup(url);
    if (!popup) {
      toast.error(t('popupBlocked'));
      return;
    }
    oauthCompletedRef.current = false;
    setOauthPending(true);
    if (oauthMonitorRef.current !== null) window.clearInterval(oauthMonitorRef.current);
    oauthMonitorRef.current = window.setInterval(() => {
      if (!popup.closed) return;
      if (oauthMonitorRef.current !== null) window.clearInterval(oauthMonitorRef.current);
      oauthMonitorRef.current = null;
      setOauthPending(false);
      if (!oauthCompletedRef.current) toast.error(t('oauthFailed'));
    }, 500);
  };

  const save = async () => {
    if (!organization || !sentryProjectId || !canManage) return;
    const project = projectsQuery.data?.find((item) => item.id === sentryProjectId);
    if (!project) return;
    try {
      await saveSelection.mutateAsync({
        organizationId: organization.id,
        organizationSlug: organization.slug,
        sentryProjectId: project.id,
        projectSlug: project.slug,
      });
      toast.success(t('selectionSaved'));
    } catch (error) {
      toast.error(isSentryReconnectRequired(error) ? t('reconnectRequired') : t('selectionFailed'));
    }
  };

  const selectionError = organizationsQuery.error ?? projectsQuery.error;
  const reconnectRequired = isSentryReconnectRequired(selectionError);
  const rateLimited = selectionError instanceof ApiError && selectionError.statusCode === 429;
  const selectedProject = projectsQuery.data?.find((item) => item.id === sentryProjectId);
  const selectionChanged =
    organization?.id !== connection?.organizationId || selectedProject?.id !== connection?.projectId;
  const isConnected = connection?.isConnected === true;
  const isLoading = connectionQuery.isPending || organizationsQuery.isFetching || projectsQuery.isFetching;
  const organizations = organizationsQuery.data ?? [];
  const projects = projectsQuery.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex min-h-7 items-center gap-2">
        <ConnectionStatusChip tone={isConnected ? 'success' : 'neutral'}>
          {isConnected ? t('connected') : t('notConnected')}
        </ConnectionStatusChip>
        <p className="text-xs text-[var(--color-fg-muted)]">{t('description')}</p>
      </div>

      {reconnectRequired ? (
        <div className="rounded-xl bg-warning/10 px-4 py-3 text-xs text-[var(--chip-warning-fg)]">
          <p>{t('reconnectRequired')}</p>
          {canManage ? (
            <button type="button" className="btn btn-warning btn-xs mt-2" onClick={connect}>
              {t('reconnect')}
            </button>
          ) : null}
        </div>
      ) : rateLimited ? (
        <p className="rounded-xl bg-warning/10 px-4 py-3 text-xs text-[var(--chip-warning-fg)]">{t('rateLimited')}</p>
      ) : selectionError ? (
        <p className="rounded-xl bg-error/10 px-4 py-3 text-xs text-[var(--chip-error-fg)]">{t('loadFailed')}</p>
      ) : null}

      <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto_auto] md:items-end">
        <div className="form-control min-w-0">
          <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]" htmlFor="prv-sentry-organization">
            {t('organization')}
          </label>
          <Select
            id="prv-sentry-organization"
            fullWidth
            size="sm"
            value={organizationId}
            onChange={(value) => {
              setOrganizationId(value);
              setSentryProjectId('');
            }}
            disabled={!canManage || organizationsQuery.isFetching || !isConnected}
            options={
              organizations.length === 0
                ? [{ value: '', label: t('organizationEmpty') }]
                : organizations.map((item) => ({ value: item.id, label: item.name }))
            }
          />
        </div>
        <div className="form-control min-w-0">
          <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]" htmlFor="prv-sentry-project">
            {t('project')}
          </label>
          <Select
            id="prv-sentry-project"
            fullWidth
            size="sm"
            value={sentryProjectId}
            onChange={setSentryProjectId}
            disabled={!canManage || projectsQuery.isFetching || !organizationId}
            options={
              projects.length === 0
                ? [{ value: '', label: t('projectEmpty') }]
                : projects.map((item) => ({ value: item.id, label: item.name }))
            }
          />
        </div>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          disabled={!canManage || oauthPending}
          onClick={connect}
        >
          {oauthPending ? <Spinner /> : null}
          {isConnected ? t('reconnect') : t('connect')}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={
            !canManage ||
            !isConnected ||
            Boolean(selectionError) ||
            !organization ||
            !selectedProject ||
            !selectionChanged ||
            saveSelection.isPending
          }
          onClick={() => void save()}
        >
          {saveSelection.isPending ? <Spinner /> : null}
          {t('saveSelection')}
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-[var(--color-fg-muted)]">
          <Spinner />
          {t('loading')}
        </div>
      ) : null}
    </div>
  );
}

/**
 * 연결된 Sentry를 목록 행으로 표시한다. 미연결이면 아무것도 렌더하지 않는다.
 *
 * Sentry는 리포지토리 레코드가 아니라 프로젝트당 하나뿐인 연결이라 `ProjectRepository`로 저장하지 않고,
 * 목록을 그릴 때만 합성 행으로 끼워 넣는다.
 */
export function SentryConnectedRow({ projectId, canManage }: SentryIntegrationProps) {
  const t = useTranslations('projects.sentry');
  const tCommon = useTranslations('common');
  const session = useMemo(() => getAuthSession(), []);
  const token = session?.token ?? null;
  const connection = useSentryConnection(token, projectId).data;
  const disconnect = useDisconnectSentryMutation(token, projectId);
  const [isDisconnectOpen, setIsDisconnectOpen] = useState(false);

  const confirmDisconnect = async () => {
    try {
      await disconnect.mutateAsync();
      setIsDisconnectOpen(false);
      toast.success(t('disconnectSuccess'));
    } catch {
      toast.error(t('disconnectFailed'));
    }
  };

  if (!connection?.isConnected) return null;

  const target =
    connection.organizationSlug && connection.projectSlug
      ? `${connection.organizationSlug} / ${connection.projectSlug}`
      : t('title');

  return (
    <>
      <li className="py-3 first:pt-0 last:pb-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {/* 아이콘 자체는 aria-hidden이므로, 접근성 이름은 감싸는 span이 든다. */}
              <span
                role="img"
                aria-label={t('title')}
                title={t('title')}
                className="shrink-0 text-[var(--color-fg-muted)]"
              >
                <SentryIcon className="h-3.5 w-3.5" />
              </span>
              <div className="truncate text-sm font-medium">{target}</div>
              <ConnectionStatusChip tone="success">{t('connected')}</ConnectionStatusChip>
            </div>
            {connection.connectedByName ? (
              <div className="mt-1 truncate text-[11px] text-[var(--color-fg-subtle)]">
                {t('connectedBy', { name: connection.connectedByName })}
              </div>
            ) : null}
          </div>
          {canManage ? (
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                aria-label={t('disconnect')}
                title={t('disconnect')}
                className="btn btn-ghost btn-xs text-error hover:bg-error/10"
                disabled={disconnect.isPending}
                onClick={() => setIsDisconnectOpen(true)}
              >
                {disconnect.isPending ? <Spinner /> : <Unlink className="h-4 w-4" aria-hidden="true" />}
              </button>
            </div>
          ) : null}
        </div>
      </li>

      <DeleteConfirmModal
        isOpen={isDisconnectOpen}
        title={t('disconnectTitle')}
        message={t('disconnectConfirm')}
        confirmLabel={t('disconnect')}
        cancelLabel={tCommon('actions.cancel')}
        isPending={disconnect.isPending}
        portal
        onConfirm={() => void confirmDisconnect()}
        onCancel={() => setIsDisconnectOpen(false)}
      />
    </>
  );
}
